import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';
import _ from 'underscore';

import { CMLUtils } from 'bundles/cml';
import { editThread } from 'bundles/discussions/actions/DropdownActions';
import DiscussionsModal from 'bundles/discussions/components/DiscussionsModal';
import ModerationReasonForm from 'bundles/discussions/components/ModerationReasonForm';
import { ContentInput, TitleInput } from 'bundles/discussions/components/NewThreadForm';
import RequiredField from 'bundles/discussions/components/RequiredField';
import RequiredFieldLabel from 'bundles/discussions/components/RequiredFieldLabel';
import { DTD_NAME } from 'bundles/discussions/constants';
import DiscussionsCMLUtils from 'bundles/discussions/utils/DiscussionsCMLUtils';

import _t from 'i18n!nls/discussions';

import 'css!./__styles__/NewThreadButton';

export type Props = {
  courseId?: string;
  question: any;
  showReasons?: boolean;
  handleClose: () => void;
};

type State = {
  title: any;
  content: any;
  showErrors: boolean;
  saving: boolean;
  saveError: boolean;
  saveErrorMessage: string;
  showModerationReasons: boolean;
  selectedOption: string;
  showInputError: boolean;
  dontNotify: boolean;
};

type Context = {
  router: any;
};

class EditThreadModal extends React.Component<Props, State> {
  declare submitButton: HTMLElement | null;

  declare titleElemRef: HTMLElement | null;

  declare contentElemRef: HTMLElement | null;

  declare reasons: HTMLElement | null;

  declare cancel: HTMLElement | null;

  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof EditThreadModal.contextTypes>;

  constructor(props: Props, context: Context) {
    super(props, context);
    const modelContent = props.question.content;

    // convert non-cml post into cml
    // can be removed if all posts are cml
    let cml = modelContent.details;
    if (!modelContent.details || !modelContent.details.typeName) {
      const value = '<co-content><text>' + (modelContent.details || '') + '</text></co-content>';
      cml = CMLUtils.create(value, DTD_NAME);
    }

    // Use the new dtdId. Can be removed once all posts have the current dtdId
    cml.definition.dtdId = DTD_NAME;

    this.state = {
      title: {
        value: modelContent.question || '',
        hasError: false,
      },
      content: {
        value: cml,
        hasError: false,
      },
      showErrors: false,
      saving: false,
      saveError: false,
      saveErrorMessage: '',
      showModerationReasons: false,
      selectedOption: '',
      showInputError: false,
      dontNotify: false,
    };

    // @ts-expect-error TSMIGRATION
    this.titleElemRef = {};
  }

  componentWillMount() {
    const { showReasons } = this.props;
    this.setState({
      showModerationReasons: !!showReasons,
    });
  }

  handleSuccess = () => {
    this.closeModal();
  };

  handleSubmit = () => {
    const { title, content, selectedOption, dontNotify } = this.state;
    const { executeAction } = this.context;
    const { question } = this.props;
    const hasAnyErrors = _(this.state).any((item) => item.hasError);

    if (hasAnyErrors) {
      if (title.hasError) {
        // @ts-expect-error TSMIGRATION
        this.titleElemRef.focus();
      } else if (content.hasError) {
        // @ts-expect-error TSMIGRATION
        this.contentElemRef.focus();
      }
      this.setState({ showErrors: true });
    } else {
      this.setState({ saving: true });

      executeAction(editThread, {
        id: question.id,
        questionId: question.questionId,
        forumType: question.forumType,
        question: title.value,
        details: content.value,
        handleSuccess: this.handleSuccess,
        handleFailure: this.handleSaveError,
        reason: selectedOption,
        dontNotify,
      });
    }
  };

  handleInputChange = (inputName: string, event: any) => {
    // @ts-expect-error TSMIGRATION
    const { [inputName]: field } = this.state;

    field.value = event.value;
    field.hasError = !event.isValid;
    this.setState((prevState) => prevState);
  };

  handleSaveError = () => {
    this.setState({
      saveError: true,
      saveErrorMessage: _t('Something went wrong. Please reload the page and try again.'),
      saving: false,
    });
  };

  handleReasonsSubmit = (selectedOption: string, dontNotify: boolean) => {
    this.setState({
      dontNotify,
      selectedOption,
      showModerationReasons: false,
    });
  };

  handleCloseModal = () => {
    this.closeModal();
  };

  handleCancel = () => {
    this.closeModal();
  };

  closeModal() {
    const { handleClose } = this.props;
    handleClose();
  }

  setTitleElemRef = (elem: any) => {
    this.titleElemRef = elem;
  };

  setContentElemRef = (elem: any) => {
    this.contentElemRef = elem;
  };

  render() {
    const { showErrors, title, content, saveErrorMessage, showModerationReasons, saving, saveError } = this.state;
    const { courseId } = this.props;

    const contentLengthWarning = DiscussionsCMLUtils.generateContentLengthWarning(content.value);

    let modalContents;
    if (showModerationReasons) {
      modalContents = (
        <ModerationReasonForm
          handleCancel={this.handleCancel}
          handleSuccess={this.handleReasonsSubmit}
          title={_t('Edit thread')}
          submitButtonText={_t('Edit Post')}
        />
      );
    } else {
      modalContents = (
        <div>
          <h1 className="c-modal-title display-1-text">{_t('Edit thread')}</h1>
          <RequiredFieldLabel />
          <div className="c-input-area">
            <RequiredField fieldName={_t('Title')} />
            <TitleInput
              id="title-input"
              hasError={showErrors && title.hasError}
              value={title.value}
              handleInputChange={this.handleInputChange}
              elemRefHook={this.setTitleElemRef}
            />
          </div>
          <div className="c-input-area">
            <RequiredField fieldName={_t('Body')} />
            <ContentInput
              id="content-body"
              courseId={courseId}
              hasError={showErrors && content.hasError}
              handleInputChange={this.handleInputChange}
              cml={content.value}
              elemRefHook={this.setContentElemRef}
            />
          </div>
          <div>
            {contentLengthWarning}
            <div role="alert" className="c-error-text c-form-error-message">
              {saveError && saveErrorMessage}
            </div>
            <div className="horizontal-box align-items-right">
              <button type="button" className="passive" onClick={this.handleCancel}>
                {_t('Cancel')}
              </button>
              &nbsp;&nbsp;
              <button
                ref={(node) => {
                  this.submitButton = node;
                }}
                disabled={saving}
                className="secondary"
                type="submit"
                onClick={this.handleSubmit}
              >
                {_t('Submit')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    const modal = (
      <DiscussionsModal modalName={_t('Edit thread')} handleClose={this.handleCloseModal}>
        {modalContents}
      </DiscussionsModal>
    );

    return <div className="rc-EditThreadForm">{modal}</div>;
  }
}

export default EditThreadModal;
